import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import HungerQuiz from '../components/HungerQuiz';
import HungerGauge from '../components/HungerGauge';
import QueFaireSi from './QueFaireSi';
import MangeTropVite from './MangeTropVite';
import ChecklistSoir from './ChecklistSoir';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map of interactive tool components
const interactiveTools = {
  'faim-ou-envie': HungerQuiz,
  'jauge-de-faim': HungerGauge,
  'que-faire-si': QueFaireSi,
  'mange-trop-vite': MangeTropVite,
  'checklist-soir': ChecklistSoir
};

export const ToolDetail = () => {
  const { slug } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTool();
  }, [slug]);

  const fetchTool = async () => {
    try {
      const response = await axios.get(`${API_URL}/tools/${slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTool(response.data);
    } catch (error) {
      console.error('Erreur chargement outil:', error);
      toast.error('Erreur lors du chargement de l\'outil');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-700 font-sans">Chargement...</p>
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="flex flex-col items-center justify-center h-screen px-6">
        <p className="text-sage-700 font-sans mb-4">Outil non trouvé</p>
        <Button onClick={() => navigate('/outils')}>Retour</Button>
      </div>
    );
  }

  const IconComponent = LucideIcons[tool.icon] || LucideIcons.Sparkles;
  const InteractiveComponent = interactiveTools[slug];

  return (
    <div className="min-h-screen pb-24" data-testid="tool-detail-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-sand-200 px-6 py-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/outils')}
          data-testid="tool-back-button"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-14 h-14 rounded-full bg-sage-100 flex items-center justify-center">
            <IconComponent className="w-7 h-7 text-sage-500" strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl font-serif font-light text-sage-700" data-testid="tool-detail-title">
            {tool.title}
          </h1>
        </div>

        {tool.description && (
          <p className="text-sage-700/70 font-sans mb-6">{tool.description}</p>
        )}

        {/* Interactive Tool or Markdown Content */}
        {InteractiveComponent ? (
          <div className="bg-white rounded-2xl border border-sage-200 shadow-sm overflow-hidden">
            <InteractiveComponent />
          </div>
        ) : (
          <div className="markdown-content prose-sage">
            <ReactMarkdown>{tool.content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolDetail;